import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell, CalendarDays, CircleUser, Folder, Gift, Home, LayoutGrid, LogOut, Map as MapIcon, Network, PlusCircle, Settings, Trophy, User, UserRound, Users, Wallet,
} from "lucide-react";

import { useAuth } from "@/lib/auth";
import { Brand } from "@/components/brand";
import { Loading } from "@/components/kit";

export const AGENT_TABS = [
  { name: "home", label: "Accueil", icon: Home },
  { name: "dossiers", label: "Dossiers", icon: Folder },
  { name: "ranking", label: "Classement", icon: Trophy },
  { name: "profile", label: "Profil", icon: User },
];

export const ADMIN_TABS = [
  { name: "admin-home", label: "Pilotage", icon: LayoutGrid },
  { name: "admin-dossiers", label: "Dossiers", icon: Folder },
  { name: "admin-finance", label: "Finances", icon: Wallet },
  { name: "admin-agents", label: "Réseau", icon: Users },
];

const AGENT_EXTRA = [
  { to: "/referral", label: "Parrainage", icon: Gift, id: "referral" },
  { to: "/commissions", label: "Mes commissions", icon: Wallet, id: "commissions" },
  { to: "/notifications", label: "Notifications", icon: Bell, id: "notifications" },
  { to: "/account", label: "Mon compte", icon: CircleUser, id: "account" },
];

const ADMIN_EXTRA = [
  { to: "/admin/planning", label: "Planning", icon: CalendarDays, id: "admin-planning" },
  { to: "/admin/clients", label: "Clients", icon: UserRound, id: "admin-clients" },
  { to: "/admin/stats", label: "Carte & répartition", icon: MapIcon, id: "admin-stats" },
  { to: "/admin/referrals", label: "Parrainages", icon: Network, id: "admin-referrals" },
  { to: "/admin/ranking", label: "Classement", icon: Trophy, id: "admin-ranking" },
  { to: "/notifications", label: "Notifications", icon: Bell, id: "notifications" },
  { to: "/admin/settings", label: "Paramètres", icon: Settings, id: "admin-settings" },
];

function SideLink({ to, label, icon: Icon, id }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${id}`}
      className={({ isActive }) =>
        `focus-ring group flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors ${
          isActive ? "bg-[#292415] text-[#D4AF37]" : "text-[#CCCCCC] hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <Icon className="h-5 w-5" aria-hidden />
      {label}
    </NavLink>
  );
}

function Sidebar({ isAdmin, user, onLogout }) {
  const navigate = useNavigate();
  const tabs = isAdmin ? ADMIN_TABS : AGENT_TABS;
  const extra = isAdmin ? ADMIN_EXTRA : AGENT_EXTRA;
  return (
    <aside data-testid="app-sidebar" className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col overflow-y-auto border-r border-[#222222] bg-[#161616] lg:flex">
      <div className="px-5 py-5">
        <Brand compact />
      </div>
      {!isAdmin && (
        <div className="px-4 pb-3">
          <button
            type="button"
            data-testid="sidebar-recommend-button"
            onClick={() => navigate("/recommend")}
            className="focus-ring flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#D4AF37] text-sm font-bold tracking-wide text-[#121212] transition-colors hover:bg-[#E2C25A]"
          >
            <PlusCircle className="h-5 w-5" /> RECOMMANDER UN CLIENT
          </button>
        </div>
      )}
      {isAdmin && <p className="t-caption px-5 pb-2 tracking-widest">ESPACE GÉRANT</p>}
      <nav aria-label="Navigation principale" className="flex flex-col gap-1 px-3">
        {tabs.map((t) => (
          <SideLink key={t.name} to={`/${t.name}`} label={t.label} icon={t.icon} id={t.name} />
        ))}
      </nav>
      <div className="mx-5 my-4 h-px bg-[#222222]" />
      <nav aria-label="Navigation secondaire" className="flex flex-col gap-1 px-3">
        {extra.map((e) => (
          <SideLink key={e.id} {...e} />
        ))}
      </nav>
      <div className="mt-auto border-t border-[#222222] p-4">
        <p className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">{user.name}</p>
        <p className="t-caption truncate">{user.email}</p>
        <button
          type="button"
          data-testid="sidebar-logout-button"
          onClick={onLogout}
          className="focus-ring mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[#555555] bg-[#2A2A2A] text-sm font-medium text-white transition-colors hover:border-[#D4AF37]/70"
        >
          <LogOut className="h-4 w-4" /> Se déconnecter
        </button>
      </div>
    </aside>
  );
}

function BottomTabs({ isAdmin }) {
  const tabs = isAdmin ? ADMIN_TABS : AGENT_TABS;
  return (
    <nav aria-label="Onglets" className="fixed inset-x-0 bottom-0 z-30 flex h-16 border-t border-[#333333] bg-[#1E1E1E]/95 backdrop-blur-md lg:hidden">
      {tabs.map((t) => (
        <NavLink
          key={t.name}
          to={`/${t.name}`}
          data-testid={`tab-${t.name}`}
          className={({ isActive }) =>
            `focus-ring flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${isActive ? "text-[#D4AF37]" : "text-[#9E9E9E] hover:text-white"}`
          }
        >
          <t.icon className="h-[22px] w-[22px]" aria-hidden />
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell({ role }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  if (user === null) return <div className="flex min-h-screen"><Loading /></div>;
  if (!user || (role !== "any" && user.role !== role)) return <Navigate to="/" replace />;
  const isAdmin = user.role === "admin";
  const onLogout = async () => {
    await logout();
    navigate("/welcome", { replace: true });
  };
  return (
    <div className="min-h-screen bg-[#121212]">
      <Sidebar isAdmin={isAdmin} user={user} onLogout={onLogout} />
      <main className="min-h-screen pb-16 lg:pb-0 lg:pl-72">
        <Outlet />
      </main>
      <BottomTabs isAdmin={isAdmin} />
    </div>
  );
}
